import React, { useEffect, useMemo, useState } from "react";
import { Card, Select, Spin, Empty } from "antd";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from "recharts";
import { DropDown } from "../../assets/icons";

const { Option } = Select;

const ALL_MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const SuperAdminRevenueChart = ({ loading = false, monthlyData = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");

  const formatCurrency = (value) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return "$0.00";
    }
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(Number(value));
    } catch (error) {
      return `$${Number(value).toFixed(2)}`;
    }
  };

  const normalizedMonthlyData = useMemo(() => {
    const asArray = Array.isArray(monthlyData) ? monthlyData : [];

    const parseMonthToDate = (row) => {
      if (row?.month) {
        const normalizedMonth = `${row.month}`.includes("-")
          ? `${row.month}-01`
          : `${row.year ?? new Date().getFullYear()}-${String(row.month).padStart(2, "0")}-01`;
        return new Date(normalizedMonth).getTime();
      }
      if (row?.year && row?.monthName) {
        return new Date(`${row.monthName} 1, ${row.year}`).getTime();
      }
      return 0;
    };

    return asArray
      .map((row, index) => {
        const revenue = Number(row?.revenue ?? 0);
        const subscriptions = Number(row?.subscriptions ?? 0);
        const label =
          row?.monthName && row?.year
            ? `${row.monthName} ${row.year}`
            : row?.month ?? "N/A";
        const sortTime = parseMonthToDate(row);
        const year =
          row?.year ??
          (sortTime ? new Date(sortTime).getFullYear() : new Date().getFullYear());
        const monthKey =
          row?.month ??
          `${year}-${String(
            row?.monthNumber ?? new Date(sortTime).getMonth() + 1
          ).padStart(2, "0")}`;

        return {
          ...row,
          id: `${monthKey}-${index}`,
          sortTime,
          revenue,
          subscriptions,
          label,
          year,
          monthKey,
        };
      })
      .sort((a, b) => a.sortTime - b.sortTime);
  }, [monthlyData]);

  const availableYears = useMemo(() => {
    const years = Array.from(
      new Set(normalizedMonthlyData.map((item) => item.year))
    );
    return years.sort((a, b) => b - a);
  }, [normalizedMonthlyData]);

  useEffect(() => {
    if (availableYears.length && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    setSelectedMonth("all");
  }, [selectedYear]);

  const yearData = useMemo(() => {
    if (!selectedYear) return [];
    return normalizedMonthlyData.filter((item) => item.year === selectedYear);
  }, [normalizedMonthlyData, selectedYear]);

  // Set of month numbers (1-12) that actually have data for the selected year
  const availableMonthNumbers = useMemo(() => {
    return new Set(yearData.map((item) => new Date(item.sortTime).getMonth() + 1));
  }, [yearData]);

  const chartData = useMemo(() => {
    if (selectedMonth === "all") return yearData;
    return yearData.filter(
      (item) => new Date(item.sortTime).getMonth() + 1 === selectedMonth
    );
  }, [yearData, selectedMonth]);

  const MonthlyRevenueTooltip = ({ active, payload }) => {
    if (active && Array.isArray(payload) && payload.length) {
      const data = payload[0]?.payload;
      if (!data) return null;
      return (
        <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-900 mb-1">{data.label}</p>
          <p className="text-sm text-gray-600 mb-1">
            Revenue:{" "}
            <span className="font-bold text-[#1E3A8A]">
              {formatCurrency(data.revenue)}
            </span>
          </p>
          <p className="text-sm text-gray-600">
            Subscriptions:{" "}
            <span className="font-semibold text-gray-900">
              {data.subscriptions}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      className="shadow-md transition-all duration-300 border-0 overflow-hidden"
      style={{
        borderRadius: "16px",
        background: "white",
        border: "1px solid #e8e8e8",
      }}
    >
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <div>
          <h3 className="text-xl font-bold text-[#1E3A8A]">
            Monthly Revenue Overview
          </h3>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 mb-1">Year</span>
            <Select
              size="middle"
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ minWidth: 110 }}
              className="revenue-select"
              suffixIcon={DropDown}
              placeholder="Select year"
            >
              {availableYears.map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 mb-1">Month</span>
            <Select
              size="middle"
              value={selectedMonth}
              onChange={setSelectedMonth}
              style={{ minWidth: 150 }}
              className="revenue-select"
              suffixIcon={DropDown}
              placeholder="Select month"
              disabled={!yearData.length}
            >
              <Option value="all">All Months</Option>
              {ALL_MONTHS.map((m) => {
                const now = new Date();
                const currentYear = now.getFullYear();
                const currentMonth = now.getMonth() + 1;

                const isFuture = selectedYear > currentYear || (selectedYear === currentYear && m.value > currentMonth);

                return (
                  <Option
                    key={m.value}
                    value={m.value}
                    disabled={isFuture}
                  >
                    {m.label}
                  </Option>
                );
              })}
            </Select>
          </div>
        </div>
      </div>

      <div style={{ width: "100%", height: 320 }}>
        {loading && !normalizedMonthlyData.length ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spin />
          </div>
        ) : !yearData.length ? (
          <div className="w-full h-full flex items-center justify-center">
            <Empty description="No monthly revenue data" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <Empty description="No data for selected month" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartData.length === 1 ? (
              <BarChart
                data={chartData}
                margin={{ top: 30, right: 30, left: 0, bottom: 30 }}
                barSize={60}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <YAxis
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: "#4B5563" }}
                />
                <Tooltip content={<MonthlyRevenueTooltip />} cursor={false} />
                <Legend verticalAlign="top" height={36} />
                <Bar dataKey="revenue" name="Revenue" fill="#1E3A8A" radius={[8, 8, 0, 0]}>
                  <LabelList
                    dataKey="revenue"
                    position="top"
                    formatter={(value) => formatCurrency(value)}
                    fill="#1E3A8A"
                  />
                </Bar>
                <Bar dataKey="subscriptions" name="Subscriptions" fill="#F97316" radius={[8, 8, 0, 0]}>
                  <LabelList
                    dataKey="subscriptions"
                    position="top"
                    formatter={(value) => `${value}`}
                    fill="#F97316"
                  />
                </Bar>
              </BarChart>
            ) : (
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="dashboardColorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="dashboardColorSubscriptions"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: "#4B5563" }}
                  interval={0}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: "#4B5563" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(value) => `${value}`}
                  tick={{ fontSize: 12, fill: "#4B5563" }}
                />
                <Tooltip content={<MonthlyRevenueTooltip />} cursor={false} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#1E3A8A"
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#dashboardColorRevenue)"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="subscriptions"
                  name="Subscriptions"
                  stroke="#F97316"
                  strokeWidth={2}
                  fillOpacity={0.3}
                  fill="url(#dashboardColorSubscriptions)"
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        )}
      </div>

      {/* Indicators below chart */}
      {chartData.length > 0 && (
        <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#1E3A8A" }}></div>
            <span className="text-xs text-gray-600">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#F97316" }}></div>
            <span className="text-xs text-gray-600">Subscriptions</span>
          </div>
        </div>
      )}
    </Card>
  );
};

export default SuperAdminRevenueChart;

